import { iEditedSinapsiDeviceApiData } from "./../model/interface/iEditedSinapsiDeviceApiData";
import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Store } from "@ngrx/store";
import { Observable } from "rxjs";
import { filter, map, take, tap } from "rxjs/operators";
import { environment } from "src/environments/environment";
import { ClientSettings } from "src/model/class/client-settings";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Device } from "src/components/shared-components/class/general-classes/device";
import { KnownDevice } from "src/model/class/general classes/known-device";
import { GatewayKind } from "src/components/shared-components/enum/enumGateway";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { DeviceValidListInfo } from "src/model/interface/device-valid-list-info";
import { SiboDeviceInfoState } from "src/model/interface/sibo-device-info-state";
import { clientSettings } from "src/state/selectors/client-settings.selectors";
import { AlertService } from "./alert.service";
import { LoggerService } from "./logger.service";
import { Gateway } from "src/components/shared-components/class/general-classes/gateway";
import { iRtuevoDeviceAdditionalInfo } from "src/model/interface/iRtuevoDeviceAdditionalInfo";
import { iEnvironmentAndDevicelInfo } from "src/model/interface/iEnvironmentAndDevicelInfo";

import { iDevicesKnown } from "src/model/interface/iDevicesKnown";
import { iDeviceAdditionalInfo } from "src/model/interface/iDeviceAdditionalInfo";
import { iDevice } from "src/model/interface/iDevice";
import { RtuevoSinapsiDevice } from "src/model/class/general classes/rtuevo-sinapsi-device";
import { AlfaSinapsiDevice } from "src/model/class/general classes/alfa-sinapsi-devices";
import { iSmartGateway } from "src/model/interface/iSmartGateway";
import { iConnectBoxConfigurationData } from "src/model/interface/iConnectBoxConfigurationData";
import { iConnectBoxConfigurationResponse } from "src/model/interface/iConnectBoxConfigurationResponse";
import { iConnectBoxProperties } from "src/model/interface/iConnectBoxProperties";
import { MqttDeviceCredentials } from "src/model/interface/mqtt-device-credentials";
import { MqttDeviceConnectToEnvironment } from "src/model/interface/mqtt-device-connection-environment";

@Injectable({
  providedIn: "root",
})
export class DeviceService {
  private _logContext: string = "Device.service";

  constructor(
    private _http: HttpClient,
    private _loggerService: LoggerService,
    private _store: Store,
    private _alertService: AlertService
  ) {}

  /**
   * Get cards from visible virtual access component
   *
   * @returns
   */
  public getDeviceValidListInfo(
    includeCards: boolean
  ): Observable<DeviceValidListInfo> {
    return this._http.get<DeviceValidListInfo>(
      environment.httpServerRoot +
        AppSettings.API_URL_DEVICE +
        "/validlist/info?includeCards=" +
        includeCards
    );
  }

  /**
   * Get device info for specific BIG device.
   * If device it not exist in anagraph, it will be create returning device new infomration
   *
   * @param deviceInfo
   * @returns
   */
  public getBIGDeviceInfo(deviceInfo: iDevice): Promise<SiboDeviceInfoState> {
    //try {
    return this._http
      .post<SiboDeviceInfoState>(
        environment.httpServerRoot + AppSettings.API_URL_DEVICE + "/big",
        deviceInfo
      )
      .pipe(
        map((siboInfoFromServer: SiboDeviceInfoState) => {
          return siboInfoFromServer;
        })
      )
      .toPromise()
      .then(
        (siboInfoFromServer: SiboDeviceInfoState) => {
          return siboInfoFromServer;
        },
        (error) => {
          this._loggerService.log(
            LogLevel.LOG_ERROR,
            this._logContext,
            "_http.post - /api/device/big ",
            "getBIGDeviceInfo()",
            "ERROR IN API CALL: " + error.message
          );
          return this._store
            .select(clientSettings)
            .pipe(
              filter((clientSetting) => clientSetting !== null),
              tap((clientSettings: ClientSettings) => {
                this._loggerService.log(
                  LogLevel.LOG_INFO,
                  this._logContext,
                  "_store.select(listGateway)",
                  "subscribeToGatewayStore()",
                  "We are using old data!!"
                );
              }),
              map((clientSettings: ClientSettings) => {
                const fallBack: SiboDeviceInfoState = {
                  id: clientSettings.idDevice,
                  name: clientSettings.deviceName,
                  propertyState: new Map(),
                };
                return fallBack;
              }),
              take(1)
            )
            .toPromise();
        }
      );
  }

  /**
   * Update params of device
   * @param deviceUpdate
   */
  public updateDeviceFromGatewayConfig(deviceUpdate: Device) {
    return this._http
      .put<Device>(
        environment.httpServerRoot + AppSettings.API_URL_DEVICE_CONFIG,
        deviceUpdate
      )
      .pipe<Device>(tap(() => {}));
  }

  /**
   * Unlock access control device sending the telegrams
   * @param idDevice
   * @returns
   */
  public unlockAccessControlDevice(idDevice: number): Observable<boolean> {
    const queryString: string = "?id=" + idDevice;

    return this._http.get<boolean>(
      environment.httpServerRoot +
        AppSettings.API_URL_DEVICE_UNLOCK +
        queryString
    );
  }

  /**
   * Delete device
   * @param idDevice
   * @returns
   */
  public deleteDevice(idDevice: number) {
    const queryString: string = "?id=" + idDevice;

    return this._http
      .delete<Device>(
        environment.httpServerRoot + AppSettings.API_URL_DEVICE + queryString
      )
      .subscribe(
        () => {
          //nothing to do if it's ok
        },
        (error) => {
          this.handleApiResponseError();
        }
      );
  }

  /**
   * Insert device
   * @param idDevice
   * @returns
   */
  public insertDevice(
    idVimarDevice: number,
    physicalAddress: string,
    description: string,
    idEnvironment: number
  ) {
    const deviceData = {
      PhysicalAddress: physicalAddress,
      Description: description,
      IdVimarDevice: idVimarDevice,
    };

    return this._http.post<Device>(
      environment.httpServerRoot + AppSettings.API_URL_DEVICE,
      deviceData
    );
  }

  /**
   * Show error message if something went wrong saving gateway settings or taking them
   */
  private async handleApiResponseError(): Promise<void> {
    this._alertService.presentAlertSomethingWrong();
  }

  /**
   * Load known devices from API
   * @param gatewayKind
   * @returns
   */
  public getKnownDevices(gatewayKind: GatewayKind): Promise<KnownDevice[]> {
    return this._http
      .get<KnownDevice[]>(
        environment.httpServerRoot +
          AppSettings.API_URL_KNOWN_DEVICE +
          "?gatewayKind=" +
          (gatewayKind as number)
      )
      .pipe(
        map((listKnownDevice: KnownDevice[]) => {
          return listKnownDevice;
        })
      )
      .toPromise();
  }

  /**
   *Get all available Sinapsi devices
   * @param idEnvironment building parts evironment ID
   * @returns array of Sinapsi devices
   */
  public getAllDevices(idEnvironment: number): Promise<Device[]> {
    return this._http
      .get<Device[]>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/${idEnvironment}/environment`
      )
      .toPromise();
  }

  /**
   *Add the extra Sinapsi devic information based on the return API/Gateway call in the case of Rtuevo
   * @param addedRtuevoDeviceInfo RTUEVO extra information
   * @returns
   */
  public addSinapsiRtuevoDeviceExtraInfo(
    addedRtuevoDeviceInfo: iRtuevoDeviceAdditionalInfo
  ): Promise<Device> {
    return this._http
      .post<Device>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/rtuevo`,
        addedRtuevoDeviceInfo
      )
      .pipe(
        map((rtuevoDeviceInfo) => {
          return rtuevoDeviceInfo;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *
   * @returns Get all unassociated sinapsi devices
   */
  public onSearchSinapsiDeviceToAssociate(): Promise<Device[]> {
    return this._http
      .get<Device[]>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/notassociated`
      )
      .toPromise();
  }

  /**
   * Send an update on device information after making a post request in the case of rtuevo sinapsi
   * @param updatedDeviceInfo extra required info to update, after adding a Sinapsi Alfa device
   * @returns
   */
  public updateDeviceEnvironment(
    updateEnvironmentAndDeviceInfo: iEnvironmentAndDevicelInfo
  ) {
    return this._http
      .put(
        `${environment.httpServerRoot}${AppSettings.API_URL_ENVIRONMENT_DEVICE}`,
        updateEnvironmentAndDeviceInfo
      )
      .toPromise();
  }

  /**
   * Add the extra Sinapsi devic information based on the return API/Gateway call in the case of an alfa device
   * @param addedAlfaDeviceInfo alfa device extra info
   * @returns
   */
  public addSinapsiAlfaDeviceExtraInfo(
    addedAlfaDeviceInfo: iDeviceAdditionalInfo
  ): Promise<Device> {
    return this._http
      .post<Device>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/alfa`,
        addedAlfaDeviceInfo
      )
      .pipe(
        map((alfaDeviceInfo) => {
          return alfaDeviceInfo;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   *Get all known  devices
   * @returns array known devices
   */
  public getAllKnownDevices(): Promise<iDevicesKnown[]> {
    return this._http
      .get<iDevicesKnown[]>(
        `${environment.httpServerRoot}${AppSettings.API_URL_KNOWN_DEVICE}/all`
      )
      .toPromise();
  }

  /**
   * Handle the deleting of a selected Sinapsi device
   * @param deviceToDeleteInfo the sinapsi device to delete ID nad environment ID
   * @returns
   */
  public deleteSinapsiDevice(deviceToDeleteInfo: iEnvironmentAndDevicelInfo) {
    return this._http
      .delete<iEnvironmentAndDevicelInfo>(
        `${environment.httpServerRoot}${AppSettings.API_URL_ENVIRONMENT_DEVICE}`,
        {
          body: deviceToDeleteInfo,
        }
      )
      .toPromise();
  }

  /**
   *Handle the editing of a Sinapsi device that falls under the categories of the Equobox Enum
   * @param editedSinapsiDeviceData edited Sinapsi device data: in this case this is an object containing the device  *description and medium which is the Equobox Enum
   * @param idDevice edited device ID
   * @returns
   */
  public updateEditedSinapsiDeviceData(
    editedSinapsiDeviceData: iEditedSinapsiDeviceApiData,
    idDevice: number
  ) {
    return this._http
      .patch<iEditedSinapsiDeviceApiData>(
        environment.httpServerRoot +
          AppSettings.API_URL_DEVICE +
          AppSettings.API_URL_RTUEVO_COUNTER +
          "/" +
          idDevice,

        editedSinapsiDeviceData
      )
      .toPromise();
  }

  /**
   * Creates the gateway instance in the case of alfa sinapsi device
   * @param addedAlfaSinapsiDevice The added sinapsi device
   * @returns
   */
  public addAlfaSinapsiDevice(
    addedAlfaSinapsiDevice: AlfaSinapsiDevice
  ): Promise<Gateway> {
    return this._http
      .post<Gateway>(
        `${environment.httpServerRoot}${AppSettings.API_URL_GATEWAY}/sinapsi/alfa`,
        addedAlfaSinapsiDevice
      )
      .pipe(
        map((gateWayData: Gateway) => {
          return gateWayData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Creates the gateway instance in the case of Rtuevo sinapsi device
   * @param addedSinapsiDevice The added sinapsi device
   * @param rtuevoArea idArea of the building in which the device is added
   * @returns
   */
  public addRtuevoSinapsiDevice(
    addedRtuevoSinapsiDevice: RtuevoSinapsiDevice,
    rtuevoArea: number
  ): Promise<Gateway> {
    return this._http
      .post<Gateway>(
        `${environment.httpServerRoot}${AppSettings.API_URL_GATEWAY}/sinapsi/rtuevo`,
        { ...addedRtuevoSinapsiDevice, rtuevoArea }
      )
      .pipe(
        map((gateWayData: Gateway) => {
          return gateWayData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Handles the posting of the data necessary to access the connect box configuration parameters
   * @param connectBoxConfigurationData the required data necessary in order to get the configuration parameters for connect box configuration
   * @returns
   */
  public addConnectBoxConfigurationData(
    connectBoxConfigurationData: iConnectBoxConfigurationData
  ): Promise<Device> {
    return this._http
      .post<Device>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/connectbox`,
        connectBoxConfigurationData
      )
      .pipe(
        map((deviceDetail: Device) => {
          return deviceDetail;
        })
      )
      .toPromise();
  }

  /**
   * Gets the parameters for connect box configuration
   * @param idDevice The device id
   * @returns Promise of the form iConnectBoxConfiguartionResponse
   */
  public getConfigurationForConnectBox(
    idDevice: number
  ): Promise<iConnectBoxConfigurationResponse> {
    const queryString = `?IDDevice=${idDevice}`;
    return this._http
      .get<iConnectBoxConfigurationResponse>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/connectbox/configuration${queryString}`
      )
      .pipe(
        map((connectBoxConfiguration: iConnectBoxConfigurationResponse) => {
          return connectBoxConfiguration;
        }),
        take(1)
      )
      .toPromise();
  }
  /*
   * Creates the gateway instance for smartgateway
   * @param addedSmartgateway The added smartgateway
   * @returns
   */
  public addSmartgateway(addedSmartgateway: iSmartGateway): Promise<Gateway> {
    return this._http
      .post<Gateway>(
        `${environment.httpServerRoot}${AppSettings.API_URL_GATEWAY}/sinapsi/smartgateway`,
        addedSmartgateway
      )
      .pipe(
        map((gateWayData: Gateway) => {
          return gateWayData;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * Add the smartgateway extra information based on the return API/Gateway call
   * @param addedSmartgatewayInfo alfa device extra info
   * @returns
   */
  public addSmartgatewayExtraInfo(
    addedSmartgatewayInfo: iDeviceAdditionalInfo
  ): Promise<Device> {
    return this._http
      .post<Device>(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/smartgateway`,
        addedSmartgatewayInfo
      )
      .pipe(
        map((smartgatewayInfo) => {
          return smartgatewayInfo;
        }),
        take(1)
      )
      .toPromise();
  }
  /**
   * Add the selected equipment properties
   * @param addedEquipmentProperty equipment property
   * @returns
   */
  public addSelectedEquipmentProperties(
    addedEquipmentProperty: iConnectBoxProperties
  ): Promise<number> {
    return this._http
      .post(
        `${environment.httpServerRoot}${AppSettings.API_URL_DEVICE}/connectbox/property`,
        addedEquipmentProperty,
        {
          observe: "response",
        }
      )
      .pipe(
        map((responseData) => {
          return responseData.status;
        }),
        take(1)
      )
      .toPromise();
  }

  /**
   * API Call to connect new device with a particular environment
   * @param bodyRequest: data for request with data obtained from precedent call
   * @returns
   */
  public connectMqttDeviceToEnvironment(
    bodyRequest: MqttDeviceConnectToEnvironment
  ): Promise<Response> {
    return this._http
      .put<Response>(
        environment.httpServerRoot + AppSettings.API_URL_ENVIRONMENT_DEVICE,
        bodyRequest
      )
      .toPromise();
  }

  /**
   * Third API call for MQTT configuration page
   * @param bodyRequest: data for request with data obtained from precedent call
   */
  public assignCredentialsMqtt(
    bodyRequest: MqttDeviceCredentials
  ): Promise<Response> {
    return this._http
      .patch<Response>(
        environment.httpServerRoot +
          AppSettings.API_URL_DEVICE +
          `/${bodyRequest.id}/credentials`,
        bodyRequest
      )
      .toPromise();
  }

  /**
   * API call to get specific device data
   *
   * @param id: device id
   */
  public getDeviceByID(id: number): Promise<Device> {
    return this._http
      .get<Array<Device>>(
        environment.httpServerRoot +
          AppSettings.API_URL_DEVICE +
          `?idDevice=${id}`
      )
      .pipe(map((listDevice) => listDevice?.[0]))
      .toPromise();
  }

  /**
   * Get list of devices by idArea
   * @param idArea
   * @returns promise with array of devices
   */
  public getDevicesByIDArea(idArea: number): Promise<Device[]> {
    return this._http
      .get<Array<Device>>(
        environment.httpServerRoot +
          AppSettings.API_URL_DEVICE +
          `?idArea=${idArea}`
      )
      .toPromise();
  }

  /**
   * API call when starting discovery for RTUEVO device
   * @param idDevice
   * @returns promise
   */
  public startDiscoveryRtuevo(idDevice: number): Promise<Object> {
    return this._http
      .post<Object>(
        environment.httpServerRoot +
          AppSettings.API_URL_DEVICE +
          `/${idDevice}/rtuevo/discovery`,
        {}
      )
      .toPromise();
  }

  /**
   * API call to save devices to other environments
   *
   * @param associationArray: array to pass for saving data
   */
  public moveDevicesToOtherEnvironments(
    associationArray: DeviceAssociation[]
  ): Promise<DeviceAssociation[]> {
    return this._http
      .put<Array<DeviceAssociation>>(
        environment.httpServerRoot +
          AppSettings.API_URL_ENVIRONMENT +
          "/move/device",
        {
          association: associationArray,
        }
      )
      .toPromise();
  }
}
